import moment from 'moment';

import {ProcessorTypes} from '../helpers/constants';
import {asyncPost, asyncLoad, asyncDelete} from '.';
import {
  objectToParams,
  formValidation,
  handleError,
  sortParam,
  rowIndexFromPage,
} from '../helpers';

export const loadPayments = ({
  customViewId = 0,
  searchString = '',
  pageInfo,
  sortInfo,
  ticketId = null,
  invoiceId = null,
}) => dispatch => {
  const params = {
    pageSize: pageInfo.pageSize,
    rowIndex: rowIndexFromPage(pageInfo),
    sort: sortParam({
      ...sortInfo,
      sortColumn:
        sortInfo.sortColumn === 'InvoiceNumber'
          ? 'Source'
          : sortInfo.sortColumn === 'CustomerName'
            ? 'Payer'
            : sortInfo.sortColumn,
    }),
  };
  if (customViewId) {
    params.customViewId = customViewId;
  }
  if (searchString) {
    params.search = searchString;
  }

  return asyncLoad({
    dispatch,
    field: 'payments',
    title: 'payments',
    url: `${
      invoiceId
        ? `/invoices/${invoiceId}`
        : ticketId
          ? `/tickets/${ticketId}`
          : ''
    }/payments?${objectToParams(params)}`,
  });
};

export const loadPayment = paymentId => dispatch =>
  asyncLoad({dispatch, title: 'payment', url: `/payments/${paymentId}`});

export const updatePayment = fields => async dispatch => {
  const formObject = formValidation.setupFormBody(fields, false);

  if (formObject.hasError) {
    handleError(formObject.errorMessage, null, dispatch);
    return false;
  }

  const {data: response} = await asyncPost({
    body: formObject.body,
    dispatch,
    params: {method: 'PUT'},
    successMessage: 'The Payment was updated successfully.',
    title: 'payment',
    url: `/payments/${fields.paymentId.value}`,
  });

  if (
    formObject.body.receiptEmails &&
    formObject.body.receiptEmails.length > 0
  ) {
    await dispatch(
      sendReceipt(
        {paymentId: response.paymentId, customerId: formObject.body.customerId},
        formObject.body.receiptEmails,
      ),
    );
  }

  return response;
};

export const deletePayment = (paymentId, gatewayType) => dispatch =>
  gatewayType === 0
    ? asyncDelete({
        dispatch,
        successMessage: 'The Payment was deleted successfully.',
        title: 'payment',
        url: `/payments/${paymentId}`,
      })
    : asyncPost({
        body: {paymentId},
        dispatch,
        successMessage: 'The Payment was deleted successfully.',
        title: 'payment',
        url: '/refunds',
        errorPrefix: 'Error processing refund',
      });

export const initiatePayment = (
  amount,
  estimateId = null,
  invoiceId = null,
  paymentType = 0,
  receiptEmails = null,
) => async dispatch => {
  const result = await asyncPost({
    body: {amount, estimateId, invoiceId, paymentType, receiptEmails},
    dispatch,
    title: 'Payment Processor',
    url: '/Payments/Charges',
  });

  if (result.error) {
    throw new Error();
  }

  return result;
};

export const getCardOnFile = (customerId, isSquare = false) => dispatch =>
  asyncLoad({
    dispatch,
    title: 'Card on File',
    url: `/customers/${customerId}/payment-profile${isSquare ? '/Square' : ''}`,
  });

export const submitPayment = ({
  auth = null,
  paymentObject,
  stripe = null,
  square = null,
}) => async dispatch => {
  const formObject = formValidation.setupFormBody(paymentObject, false);

  if (formObject.hasError) {
    handleError(formObject.errorMessage, null, dispatch);
    return {error: true, formError: true};
  }

  if (stripe && stripe.obj && stripe.card) {
    try {
      const stripeResult = await stripe.obj.createSource(stripe.card, {
        amount: Math.round(formObject.body.amount * 100),
        currency: formObject.body.stripeCurrency,
        type: 'card',
      });

      if (stripeResult.error) {
        if (stripeResult.error.errorMessage)
          handleError(stripeResult.error.errorMessage, null, dispatch);
        return {error: true};
      }

      const {card} = stripeResult.source;

      var tokenResult = await stripe.obj.createToken(stripe.card);

      if (tokenResult.error) {
        handleError(tokenResult.errorMessage, null, dispatch);
        return {error: true};
      }
      const {token} = tokenResult;

      formObject.body.token = token.id;
      formObject.body.stripePayment = {
        cardType: card.brand,
        expirationYear: card.exp_year,
        expirationMonth: card.exp_month,
        lastFourDigits: card.last4,
        sourceId: stripeResult.source.id,
      };
    } catch (error) {
      handleError(error, null, dispatch);
      return {error: true};
    }
  } else if (auth) {
    formObject.body.authPayment = auth;
  } else if (square) {
    formObject.body.profileId =
      square.selectedCardId && square.selectedCardId !== '0'
        ? Number(square.selectedCardId)
        : null;
    formObject.body.squareNonce = square.nonce || null;
  }

  const result = asyncPost({
    body: formObject.body,
    dispatch,
    params: {method: 'PUT'},
    successMessage: 'The Payment has been received successfully!',
    title: 'Payment',
    url: `/payments/charges/${stripe ? 'stripe' : auth ? 'authnet' : 'square'}`,
    errorPrefix: 'Error processing payment',
  });

  if (result.error) {
    return {error: true};
  }

  return result;
};

export const savePayment = (
  body,
  processor = ProcessorTypes.WePay,
) => dispatch =>
  asyncPost({
    body,
    dispatch,
    params: {method: 'PUT'},
    url: `/payments/charges/${processor.toLowerCase()}`,
  });

export const sendReceipt = (
  {paymentId, customerId},
  receiptEmails,
) => dispatch =>
  asyncPost({
    body: {
      customerId,
      receiptEmails: receiptEmails,
    },
    dispatch,
    url: `/payments/${paymentId}/receipts`,
    successMessage: 'Success!  The receipt should be received shortly',
  });

export const saveManualPayment = fields => async dispatch => {
  const formObject = formValidation.setupFormBody(fields, false);

  if (formObject.hasError) {
    handleError(formObject.errorMessage, null, dispatch);
    return false;
  }

  if (formObject.body.expiration) {
    const exp = formObject.body.expiration.split('/');

    formObject.body.expiration = moment([exp[1], Number(exp[0]) - 1, 1]);
  }

  const {data: response} = await asyncPost({
    body: formObject.body,
    dispatch,
    successMessage: 'The Payment has been received successfully!',
    title: 'Payment',
    url: '/payments',
  });

  if (
    formObject.body.receiptEmails &&
    formObject.body.receiptEmails.length > 0
  ) {
    await dispatch(
      sendReceipt(
        {paymentId: response.paymentId, customerId: formObject.body.customerId},
        formObject.body.receiptEmails,
      ),
    );
  }

  return response;
};

export const updateQbStatus = (payments, status) => dispatch =>
  asyncPost({
    body: {
      updates: payments.map(x => ({
        paymentId: x.paymentId,
        status,
      })),
    },
    dispatch,
    params: {method: 'PUT'},
    url: '/payments/quickbooks/status',
    successMessage: 'QuickBooks status updated',
  });

export const resetQbFlags = payments => dispatch =>
  asyncPost({
    body: {
      updates: payments.map(x => ({
        paymentId: x.paymentId,
        resetFlags: true,
      })),
    },
    dispatch,
    params: {method: 'PUT'},
    url: '/payments/quickbooks/status',
    successMessage: 'QuickBooks flags reset',
  });



// WEBPACK FOOTER //
// ./src/actions/paymentActions.js